import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import { useI18n } from '../../i18n/index.jsx';
import { Alert, Field, PasswordInput } from '../../components/ui.jsx';
import PasswordStrength from '../../components/PasswordStrength.jsx';
import AuthLayout from '../../components/auth/AuthLayout.jsx';
import CodeStep from '../../components/auth/CodeStep.jsx';
import { useCodeFlow } from '../../components/auth/useCodeFlow.js';
import { afterSignIn } from './postLogin.js';

// "Forgot password" with a 6-digit emailed code: email → code → new
// password → signed in. The screen is the same whether or not the address
// has an account (only members actually receive a code).
export default function ForgotPassword() {
  const { t, errorText } = useI18n();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const flow = useCodeFlow('reset');
  const [password, setPassword] = useState('');
  const below = <Link to="/login">{t('auth.backToLogin')}</Link>;

  async function save(e) {
    e.preventDefault();
    flow.setError('');
    if (password.length < 8) return flow.setError(t('errors.password_too_short'));
    flow.setBusy(true);
    try {
      const { token, user } = await api('/auth/reset-password', { method: 'POST', body: { ticket: flow.ticket, password } });
      signIn(token, user);
      navigate(afterSignIn(user, null), { replace: true });
    } catch (err) {
      flow.setError(errorText(err));
      if (err.code === 'ticket_invalid') flow.changeEmail();
      flow.setBusy(false);
    }
  }

  if (flow.step === 'code') {
    return (
      <AuthLayout titleKey="auth.codeTitle" title={t('auth.codeTitle')} lead={t('auth.resetCodeLead')} below={below}>
        <CodeStep flow={flow} onResend={() => flow.send()} />
      </AuthLayout>
    );
  }
  if (flow.step === 'final') {
    return (
      <AuthLayout titleKey="auth.resetTitle" title={t('auth.resetTitle')} lead={flow.email.trim()} below={below}>
        <form onSubmit={save} className="form">
          <Field label={t('auth.newPassword')} htmlFor="rp-password">
            <PasswordInput id="rp-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required minLength={8} autoFocus />
            <PasswordStrength password={password} />
          </Field>
          <Alert>{flow.error}</Alert>
          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={flow.busy}>
            {flow.busy ? t('common.saving') : t('auth.resetSubmit')}
          </button>
        </form>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout titleKey="auth.forgotTitle" title={t('auth.forgotTitle')} lead={t('auth.forgotLead')} below={below}>
      <form onSubmit={(e) => { e.preventDefault(); flow.send(); }} className="form">
        <Field label={t('auth.email')} htmlFor="fp-email">
          <input id="fp-email" type="email" value={flow.email} onChange={(e) => flow.setEmail(e.target.value)} autoComplete="email" required />
        </Field>
        <Alert>{flow.error}</Alert>
        <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={flow.busy}>
          {flow.busy ? t('common.sending') : t('auth.sendCode')}
        </button>
      </form>
    </AuthLayout>
  );
}
